'use strict'
/**
 * PLErrorBoundary · brand-aligned error boundary
 * ───────────────────────────────────────────────────────────────────────────
 * Cinceladura cables-v3 · 2026-05-28 ·  PWA jamás debe white-screenear.
 * Si cualquier hijo lanza al renderizar, mostramos fallback cream+terracota
 * con CTA "Reintentar" (reset interno) + "Recargar app" (window.reload).
 *
 * Wrap pattern:
 *   <PLErrorBoundary scope="today">
 *     <TodayPL />
 *   </PLErrorBoundary>
 *
 * El `scope` se loguea en console.error para identificar qué pantalla petó.
 * Sentry ya captura el global; este boundary local protege rutas pesadas
 * con cargas de datos (Today, Calendar, Hub, Progress) sin propagar al root.
 */

import React from 'react'
import { esErrorDeTrozo, recargaUnaVez } from '../../lib/cargaDeTrozo'

const FALLBACK_STYLES = {
  outer: {
    minHeight: '100vh',
    background: 'var(--prime-cream, #FAF9F5)',
    color: 'var(--pl-ink)',
    fontFamily: 'var(--font-sans, Inter, system-ui, sans-serif)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    flexDirection: 'column',
    gap: 16,
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 500,
    letterSpacing: '-0.01em',
    color: 'var(--pl-ink)',
  },
  body: {
    margin: 0,
    maxWidth: 320,
    fontSize: 14,
    lineHeight: 1.55,
    color: 'var(--prime-muted-pl, #777)',
  },
  ctaPrimary: {
    marginTop: 8,
    height: 48,
    padding: '0 24px',
    background: 'var(--prime-terracota, #C4603C)',
    color: 'var(--prime-cream, #FAF9F5)',
    border: 'none',
    borderRadius: 12,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  ctaSecondary: {
    height: 44,
    padding: '0 20px',
    background: 'transparent',
    border: '1px solid color-mix(in srgb, var(--prime-terracota, #C4603C) 35%, transparent)',
    color: 'var(--prime-terracota, #C4603C)',
    borderRadius: 12,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  diag: {
    marginTop: 12,
    fontSize: 11,
    color: 'var(--prime-muted-pl, #999)',
    fontFamily: 'monospace',
    opacity: 0.7,
    maxWidth: 320,
    wordBreak: 'break-word',
  },
}

class PLErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, resetKey: 0 }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    const scope = this.props.scope || 'app'
    // Sentry global captura igual · este log identifica el scope local.
    // eslint-disable-next-line no-console
    console.error(`[PLErrorBoundary · ${scope}]`, error, errorInfo?.componentStack)
    this.setState({ errorInfo })
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, errorInfo, scope) } catch (_) {}
    }
  }

  handleRetry = () => {
    this.setState((s) => ({ hasError: false, error: null, errorInfo: null, resetKey: s.resetKey + 1 }))
  }

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  render() {
    if (this.state.hasError) {
      const scope = this.props.scope || 'app'
      // COLA:alta-termina-en-pantalla-de-error · SI LO QUE FALLÓ ES UN TROZO, «Reintentar» no sirve: vuelve a pedir el mismo hash que ya no existe, y por
      // eso REALIDAD vio que sólo «Recargar la app» salía de ahí (2026-09-23, qaalfa, justo después del alta — con el plan YA escrito).
      // El boundary de la app ya hacía esto; el de RUTA, que es el que atrapa primero (está dentro de la ruta, pegado al componente lazy), no.
      // `recargar` es la costura del guard: jsdom no deja sustituir `window.location`.
      // 🔴 Y el orden importa: lo que se PINTA se decide por el error, no por «¿he recargado?». La primera versión encadenaba las dos cosas
      // (`esErrorDeTrozo(e) && recargaUnaVez(...)`) y en el SEGUNDO render —React vuelve a renderizar el estado de error al guardar el `errorInfo`—
      // `recargaUnaVez` ya devolvía false por su propia ventana y la pantalla caía otra vez en «Algo no se cargó bien». Lo cazó el guard.
      if (esErrorDeTrozo(this.state.error)) {
        recargaUnaVez(`el boundary de ${scope} vio un error de trozo`, this.props.recargar)
        return (
          <div data-testid="boundary-recargando" data-pl-error-scope={scope} style={FALLBACK_STYLES.outer}>
            <h2 style={FALLBACK_STYLES.title}>Hay una versión nueva</h2>
            <p style={FALLBACK_STYLES.body}>La estoy cargando. No hace falta que hagas nada.</p>
          </div>
        )
      }
      const msg = this.state.error?.message || 'Error inesperado al renderizar'
      return (
        <div role="alert" data-theme="light" data-pl-error-scope={scope} style={FALLBACK_STYLES.outer}>
          <img src="/logo-mark.svg" alt="" width={48} height={48} aria-hidden />
          <h2 style={FALLBACK_STYLES.title}>Algo no se cargó bien</h2>
          <p style={FALLBACK_STYLES.body}>
            Hemos guardado el detalle del problema. Puedes reintentar esta pantalla
            o recargar la app · tus datos están a salvo.
          </p>
          <button type="button" onClick={this.handleRetry} style={FALLBACK_STYLES.ctaPrimary}>
            Reintentar
          </button>
          <button type="button" onClick={this.handleReload} style={FALLBACK_STYLES.ctaSecondary}>
            Recargar la app
          </button>
          {/* Diag visible solo si query ?diag=1 · evita filtrar stacks a usuarios */}
          {typeof window !== 'undefined' && window.location?.search?.includes('diag=1') && (
            <div style={FALLBACK_STYLES.diag}>
              scope=<strong>{scope}</strong>
              <br />
              {msg}
            </div>
          )}
        </div>
      )
    }
    // key cambia tras retry para forzar remount limpio del subtree.
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>
  }
}

export default PLErrorBoundary
